import React from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/ds';
import { statusToBadgeVariant, statusToStage, PIPELINE_STAGE_LABELS } from '@/lib/contracting/statusMapping';

/**
 * RecentRequestsStrip — the shared "Most Recent" list at the bottom of both
 * Agent and Agency Command Center contracting pipelines. Extracted in
 * Phase 18.7 so status-badge colouring and layout live in one place.
 *
 * Responsibilities (kept narrow):
 *   • Render up to `maxItems` recent request rows from a pre-fetched list.
 *   • Colour each row's status badge via `statusToBadgeVariant()` — the
 *     single source of truth for contracting status colour.
 *   • Emit stable `data-testid` per row using the supplied `testIdPrefix`.
 *
 * Deliberately NOT responsibilities:
 *   • Data fetching (parent owns the API call).
 *   • Business-stage calculation (parent already has requests; badges are
 *     driven by the shared taxonomy utility).
 *   • Empty-state chrome — parent decides whether the whole section renders.
 *
 * @param {Array} requests            — contract request records
 * @param {number} [maxItems=3]       — number of rows to render
 * @param {string} testIdPrefix       — 'agent-recent-request' or 'agency-recent-request'
 * @param {(r)=>string} [metaFormatter] — optional formatter for the secondary
 *   meta line under the carrier name. Defaults to "{contract_type} · {level}".
 */
const defaultMetaFormatter = (r) =>
  [r?.contract_type_name, r?.level_name].filter(Boolean).join(' · ');

export const RecentRequestsStrip = ({
  requests = [],
  maxItems = 3,
  testIdPrefix,
  metaFormatter = defaultMetaFormatter,
}) => {
  if (!requests.length) return null;
  const rows = requests.slice(0, maxItems);
  return (
    <div className="mt-5 pt-4 border-t" style={{ borderColor: 'var(--kr-border)' }}>
      <div
        className="text-[10px] font-semibold uppercase tracking-wider mb-2"
        style={{ color: 'var(--kr-subtle)' }}
      >
        Most Recent
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <Link
            key={r.request_id}
            to={`/requests/${r.request_id}`}
            className="flex items-center justify-between p-2 -mx-2 rounded-md hover:bg-slate-50 transition-colors"
            data-testid={`${testIdPrefix}-${r.request_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--kr-navy-900)' }}>
                {r.carrier_name}
              </div>
              <div className="text-[11px]" style={{ color: 'var(--kr-muted)' }}>
                {metaFormatter(r)}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {/* Phase 27.53.I — Show the pipeline STAGE badge on top so
                 tile counts visually reconcile with this row list. The
                 raw status stays underneath for KR-side auditors. */}
              <StatusBadge variant={statusToBadgeVariant(r.current_status)}>
                {PIPELINE_STAGE_LABELS[statusToStage(r.current_status)] || 'Other'}
              </StatusBadge>
              <span className="text-[10px] text-slate-400 lowercase">
                {r.current_status?.replace(/_/g, ' ')}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default RecentRequestsStrip;
